import React from "react";
import { ChevronDown } from "lucide-react";

interface CustomNameFieldProps {
  /** The label the dropdown it replaced was wearing, so the row does not move. */
  label: string;
  value: string;
  placeholder: string;
  /** Names the chevron for anyone who cannot see it — "Pick from the suggested plans". */
  dismissLabel: string;
  onValueChange: (value: string) => void;
  /** Back to the list. The caller decides what happens to what was typed. */
  onDismiss: () => void;
}

/**
 * A name field once it is being typed rather than picked — what the create
 * dialogs swap their suggestions dropdown for when "Add custom …" is chosen.
 *
 * It wears the dropdown's trigger — same box, same chevron, same height — so
 * swapping the control does not move the form under the cursor. The border is
 * `focus-within` because what is focused is the input inside, not the box
 * drawing it.
 *
 * Shared by the plan and service dialogs rather than copied into each: the two
 * sit one tab apart on the same screen, and a second copy of a control is how
 * they would quietly stop looking alike.
 */
const CustomNameField: React.FC<CustomNameFieldProps> = ({
  label,
  value,
  placeholder,
  dismissLabel,
  onValueChange,
  onDismiss,
}) => {
  const fieldId = React.useId();

  return (
    <div>
      <label
        htmlFor={fieldId}
        className="mb-1.5 block text-[13px] 3xl:text-[14px] text-ink"
      >
        {label}
      </label>
      <div className="flex items-center gap-2 w-full rounded-[6px] border border-line bg-white px-3.5 py-2.5 3xl:py-3 text-[14px] 3xl:text-[15px] transition-colors focus-within:border-brand">
        <input
          id={fieldId}
          type="text"
          // The row was pressed to type into this, so the caret starts here
          // rather than one tab away.
          autoFocus
          value={value}
          placeholder={placeholder}
          onChange={(event) => onValueChange(event.target.value)}
          className="min-w-0 flex-1 bg-transparent text-ink placeholder:text-ink-subtle focus:outline-none"
        />
        <button
          type="button"
          aria-label={dismissLabel}
          onClick={onDismiss}
          className="flex-shrink-0 text-ink-muted hover:text-ink transition-colors"
        >
          <ChevronDown aria-hidden="true" className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CustomNameField;
